import { useMemo, useRef, useState } from "react";
import {
  Box,
  PaginationProvider,
  SortProvider,
  NameSection,
  PropertyPreviewNavigationTabs,
  FeaturesAndAmenitiesList,
  AdditionalInfoSection,
  MapSection,
  DevelopInfoSection,
  InventorySectionProvider,
  DropdownOptions,
  TooltipPopup,
  ModalProvider,
} from "dash-ui";
import {
  GetPropertyByIdQuery,
  PropertyTransactionTypesEnum,
  useGetAmenitiesQuery,
} from "gql/generated.graphql";
import Header from "flow-components/Header/Header";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { useTranslation } from "react-i18next";
import { GeneralInfoSection as PropertyGeneralInfoSection } from "v2/components/property-general-info-section";
import moment from "moment";

import SlideShowSection from "./SlideShowSection/SlideShowSection";
import InventoryTableSection from "./InventoryTableSection/InventoryTableSection";
import MarketingFilesSection from "./MarketingFilesSection/MarketingFilesSection";
import PropertySidebar from "./PropertySidebar";
import PropertyHistoryModalWithPagination from "./PropertyHistoryModalWithPagination";

interface PropertyUserPreviewPageContentProps {
  property: GetPropertyByIdQuery["getPropertyById"];
  isSeeker?: boolean;
}

function PropertyUserPreviewPageContent({
  property,
  isSeeker = false,
}: PropertyUserPreviewPageContentProps) {
  const { t } = useTranslation();
  const generalInfoRef = useRef(null);
  const additionalInfoRef = useRef(null);
  const marketingFilesInfoRef = useRef(null);
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);

  useBrowserTabTitle(`${property.name} ${t("common:labels.dashSuffix")}`);

  const { data: amenitiesData, loading: amenitiesLoading } =
    useGetAmenitiesQuery({
      variables: {
        filter: { propertyId: property._id },
        pagination: { allowLimit: false },
      },
    });

  const amenities = useMemo(
    () => amenitiesData?.getAmenities.collection || [],
    [amenitiesData]
  );

  const isRent = useMemo(
    () => property?.transactionType === PropertyTransactionTypesEnum.ForRent,
    [property]
  );

  const fromNow = useMemo(() => {
    const now = moment();
    const date = property?.lastUpdatedAtByChangelog;
    const diff = now.diff(date, "days");
    if (diff < 1) return "updated today";
    return `updated ${diff} days ago`;
  }, [property]);

  return (
    <ModalProvider>
      <>
        <DropdownOptions />
        <TooltipPopup />
        <PaginationProvider perPage={10}>
          <PropertyHistoryModalWithPagination
            isOpen={isOpenHistoryModal}
            onClose={() => setIsOpenHistoryModal(false)}
            propertyId={property._id}
            hasChangelog={property?.hasChangelog}
          />
        </PaginationProvider>
        <Header />
        <div className="flex flex-col bg-slate-50">
          <div className="w-full flex flex-col max-w-screen-lg mx-auto px-4 py-16 gap-8 text-slate-700">
            <SlideShowSection property={property} />
            <NameSection property={property} />
            <div className="flex gap-4 w-full max-w-screen-lg items-start">
              <div className="flex flex-col gap-8 w-full md:max-w-[calc(100%-272px)]">
                <PropertyPreviewNavigationTabs
                  top={48}
                  navigationElementsRefs={[
                    generalInfoRef,
                    additionalInfoRef,
                    marketingFilesInfoRef,
                  ]}
                  isSeeker={isSeeker}
                  brandedFilesReady={property?.brandedFilesReady || false}
                />
                <Box w="100%">
                  <PropertyGeneralInfoSection
                    data={{
                      highlights: property.projectDetails,
                      overview: property.overview,
                      brokerCommission: property.brokerCommission,
                    }}
                    containerRef={generalInfoRef}
                    isSeeker={isSeeker}
                  />
                  <FeaturesAndAmenitiesList
                    amenities={amenities}
                    loading={amenitiesLoading}
                  />
                  <PaginationProvider perPage={5}>
                    <SortProvider>
                      <InventorySectionProvider>
                        <InventoryTableSection filter={{ property }} />
                      </InventorySectionProvider>
                    </SortProvider>
                  </PaginationProvider>
                  <AdditionalInfoSection
                    containerRef={additionalInfoRef}
                    property={property}
                  />
                  <MapSection property={property} />
                  <DevelopInfoSection property={property} />

                  {/* Marketing Files */}
                  {(!isSeeker || property.brandedFilesReady) && (
                    <MarketingFilesSection
                      property={property}
                      containerRef={marketingFilesInfoRef}
                      allowDownloads={isSeeker && !!property.brandedFilesReady}
                      isSeeker={isSeeker}
                    />
                  )}
                </Box>
              </div>

              {/* Sidebar */}
              <PropertySidebar
                property={property}
                isSeeker={isSeeker}
                isRent={isRent}
                fromNow={fromNow}
                onHistoryClick={() => setIsOpenHistoryModal(true)}
              />
            </div>
          </div>
        </div>
      </>
    </ModalProvider>
  );
}

export default PropertyUserPreviewPageContent;
